import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { API_URL } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

interface SnippetUsageItem {
  prompt_registry_id: number;
  prompt_name: string;
  version_numbers: number[];
}

interface LabelUsageItem {
  prompt_registry_id: number;
  prompt_name: string;
  label_name: string;
}

interface SnippetUsageResponse {
  success: boolean;
  message: string;
  snippet_name: string;
  snippet_usage: SnippetUsageItem[];
  total_prompts_using_snippet: number;
  label_usage: LabelUsageItem[];
  total_labels_using_snippet: number;
}

export const useSnippetUsage = ({
  promptRegistryId,
  promptVersionNumber,
  enabled = true,
}: {
  promptRegistryId?: number;
  promptVersionNumber?: number;
  enabled?: boolean;
}) => {
  const { workspaceId } = useParams();
  const auth = useAuth();
  const userToken = auth?.userToken;

  const url = new URL(
    `${API_URL}/api/dashboard/v2/workspaces/${workspaceId}/prompt-templates/${promptRegistryId}/snippet-usage`,
  );

  if (promptVersionNumber) {
    url.searchParams.set("prompt_version_number", promptVersionNumber.toString());
  }

  return useQuery<SnippetUsageResponse>({
    queryKey: ["snippet-usage", workspaceId, promptRegistryId, promptVersionNumber],
    queryFn: () =>
      fetch(url.toString(), {
        headers: jsonAuthHeaders(userToken!),
        method: "GET",
      }).then(res => handleResponse(res)),
    enabled: enabled && !!workspaceId && !!promptRegistryId && !!userToken,
    staleTime: 5 * 60 * 1000,
  });
};
